import React, { MouseEventHandler } from "react";
import { useTranslation } from 'react-i18next';
import { Destination } from "../common/ObjectTypes";
import { Delete, SortingArrows } from "../common/ProjectIcons";
import Spinner from "../common/Spinner";
import NoResults from "../common/NoResults";
import { tableClasses, buttonClasses } from "../common/tailwindClassnames";

type ConnectionDestinationTableProps = {
    newConnection: boolean,
    destinations: Destination[],
    existingDestPhase0: Destination[],
    existingDestPhase1: Destination[],
    existingDestPhase2: Destination[],
    existingDestPhase3: Destination[],
    addExistingDestinations: boolean,
    updatingDestinations: boolean,
    onAddExistingDestinations: MouseEventHandler,
    onClickAddDestination: Function,
    onViewDetails: Function,
    onRemoveDestination: Function,
    onSelectDestination: Function,
    searchTerm?: string,
}

const ConnectionDestinationTable = ({
    newConnection,
    destinations,
    existingDestPhase0,
    existingDestPhase1,
    existingDestPhase2,
    existingDestPhase3,
    addExistingDestinations,
    updatingDestinations,
    onAddExistingDestinations,
    onClickAddDestination,
    onViewDetails,
    onRemoveDestination,
    onSelectDestination,
    searchTerm = ""
}: ConnectionDestinationTableProps) => {
    const { t } = useTranslation();
    let destlist = addExistingDestinations ? existingDestPhase0 : destinations;

    // Helper: check if any destination in phase 1, 2, or 3 matches the search term (name or base_url)
    const lowerSearch = searchTerm.trim().toLowerCase();
    function matchesSearch(dest: Destination) {
        return (
            dest.name.toLowerCase().includes(lowerSearch) ||
            dest.base_url.toLowerCase().includes(lowerSearch)
        );
    }
    const hasOtherPhaseResultsInSearch =
        !!lowerSearch && ([...existingDestPhase1, ...existingDestPhase2, ...existingDestPhase3].some(matchesSearch));

    return (
        <div>
            <div className="mb-3">
                <label className="text-off-black dark:text-white transition-all duration-300">{addExistingDestinations ? "Select existing destinations to add to the connection" : `Connected destinations: ${destinations.length}`}</label>
            </div>
            <table className={tableClasses.container}>
                <thead className={tableClasses.header}>
                    <tr>
                        {addExistingDestinations && <th></th>}
                        <th className={tableClasses.headerCell}>{t("Status")}</th>
                        <th className={tableClasses.headerCell}>{t("Name")}</th>
                        <th className={`${tableClasses.headerCell} flex items-center gap-2`}>
                            {t("Destination")}
                            <span className={tableClasses.sortIcon}><SortingArrows /></span>
                        </th>
                        <th className={tableClasses.headerCell}>{t("Workspace")}</th>
                        <th className={tableClasses.headerCell}>{t("Technology")}</th>
                        {!addExistingDestinations && <th></th>}
                    </tr>
                </thead>

                <tbody className="text-sm dark:text-white transition-all duration-300">
                    {addExistingDestinations && (
                        existingDestPhase3.map((destination: Destination, index: number) => {
                            let dest_url: string | URL;
                            try {
                                dest_url = new URL(destination.base_url).hostname;
                            } catch (err) {
                                dest_url = "";
                            }
                            return (
                                <tr key={destination.id} className={tableClasses.row}>
                                    <td className={tableClasses.cell}>
                                        <input
                                            className="accent-dark-green cursor-pointer"
                                            type="checkbox"
                                            value={destination.id}
                                            id={destination.id}
                                            name={destination.name}
                                            checked={existingDestPhase2.map((a: any) => a.id).includes(destination.id)}
                                            onChange={() => onSelectDestination(destination)}
                                        />
                                    </td>
                                    <td className={tableClasses.cell}>------</td>
                                    <td className={tableClasses.cell}>{destination.name}</td>
                                    <td
                                        className={`${tableClasses.cell} text-blue-basic dark:text-dark-blue-300 cursor-pointer`}
                                        onClick={() => onViewDetails(destination.id)}
                                    >
                                        {dest_url}
                                    </td>
                                    <td className={tableClasses.cell}>{destination.owner.name}</td>
                                    <td className={tableClasses.cell}>{destination.type.name}</td>
                                </tr>
                            );
                        })
                    )}
                    {updatingDestinations && (
                        <tr>
                            <td colSpan={6} className={tableClasses.cell}>
                                <Spinner />
                            </td>
                        </tr>)}
                    {(destlist.length < 1 && !hasOtherPhaseResultsInSearch) ? (
                        <tr>
                            <td colSpan={6} className={`${tableClasses.cell} text-left text-secondary-gray dark:text-gray-lines`}>
                                {addExistingDestinations ?
                                    <NoResults
                                        headerMsg={t("No Results Found")}
                                        labelMsg={t("You may need to adjust your search or filters to find what you are looking for")}
                                    />
                                    : t("No Destinations connected to this provider.")}
                            </td>
                        </tr>
                    ) : (
                        !updatingDestinations && destlist.map((destination: Destination) => {
                            let dest_url: string | URL;
                            try {
                                dest_url = new URL(destination.base_url).hostname;
                            } catch (err) {
                                dest_url = "";
                            }
                            return (
                                <tr key={destination.id} className={tableClasses.row}>
                                    {addExistingDestinations && (
                                        <td className={tableClasses.cell}>
                                            <input
                                                className="accent-dark-green cursor-pointer"
                                                type="checkbox"
                                                value={destination.id}
                                                id={destination.id}
                                                name={destination.name}
                                                checked={existingDestPhase1.map((a: any) => a.id).includes(destination.id)}
                                                onChange={() => onSelectDestination(destination)}
                                            />
                                        </td>
                                    )}
                                    <td className={tableClasses.cell}>------</td>
                                    <td className={tableClasses.cell}>{destination.name}</td>
                                    <td
                                        className={`${tableClasses.cell} text-blue-basic dark:text-dark-blue-300 cursor-pointer`}
                                        onClick={() => onViewDetails(destination.id)}
                                    >
                                        {dest_url}
                                    </td>
                                    <td className={tableClasses.cell}>{destination.owner.name}</td>
                                    <td className={tableClasses.cell}>{destination.type.name}</td>
                                    {!addExistingDestinations && (
                                        <td className={`${tableClasses.cell} w-16`}>
                                            <div onClick={() => onRemoveDestination(destination)} className="bg-very-light-orange dark:bg-orange-900 p-1 rounded-sm cursor-pointer transition-all duration-300">
                                                <Delete />
                                            </div>
                                        </td>
                                    )}
                                </tr>
                            );
                        })

                    )}
                    {(!newConnection && addExistingDestinations) && (
                        <tr className={tableClasses.row}>
                            <td colSpan={6} className={tableClasses.cell}>
                                <button onClick={onAddExistingDestinations}
                                    className={(existingDestPhase1.length > 0 || existingDestPhase2.length > 0)
                                        ? buttonClasses.primary
                                        : buttonClasses.disabled}>
                                    Add Destinations
                                </button>
                                <button onClick={() => onClickAddDestination(true)}
                                    className={buttonClasses.secondary}>
                                    Cancel
                                </button>
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
            {!addExistingDestinations && <div className="flex flex-row mt-3 transition-all duration-300">
                <button onClick={() => onClickAddDestination(false)}
                    className={`${buttonClasses.primary} mr-2 text-sm py-2 px-3`}>
                    + New Destination
                </button>
                <button data-testid="add-existing-destination-button" aria-label="add_existing"
                    onClick={() => onClickAddDestination(true)}
                    className="bg-light-green dark:bg-dark-green-900 text-dark-green dark:text-dark-green-100 font-semibold rounded py-2 px-3 text-sm transition-all duration-300">
                    + Existing Destination
                </button>
            </div>}
        </div>
    );
};

export default ConnectionDestinationTable;